import Fuse from 'fuse.js'
import { useEffect, useMemo, useState } from 'react'
import * as m from '~/paraglide/messages'
import {
  ALL_COUNTRIES,
  CURATED_COUNTRIES,
  countryDisplayName,
  isKnownCountry,
  normalizeCountry,
} from '~/shared/lib/countries'
import { Input } from '~/shared/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '~/shared/ui/select'

interface CountrySelectProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  id?: string
  name?: string
}

export function CountrySelect({
  value,
  onChange,
  onBlur,
  placeholder,
  required,
  disabled,
  id,
  name,
}: CountrySelectProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const canonical = value ? normalizeCountry(value) : ''
  const selectedValue = isKnownCountry(canonical) ? canonical : ''

  useEffect(() => {
    if (selectedValue && selectedValue !== value) onChange(selectedValue)
  }, [selectedValue, value, onChange])

  const countries = useMemo(
    () =>
      ALL_COUNTRIES.map((country) => ({
        ...country,
        label: countryDisplayName(country.name),
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [],
  )

  const fuse = useMemo(
    () =>
      new Fuse(countries, {
        keys: ['label', 'name', 'code'],
        threshold: 0.3,
        includeScore: true,
      }),
    [countries],
  )

  const filteredCountries = useMemo(() => {
    if (!searchQuery) {
      return countries
    }
    return fuse.search(searchQuery).map((result) => result.item)
  }, [searchQuery, fuse])

  const showCuratedSection = useMemo(() => {
    if (!searchQuery) return true
    return CURATED_COUNTRIES.some((curated) =>
      filteredCountries.some((filtered) => filtered.code === curated.code),
    )
  }, [searchQuery, filteredCountries])

  const filteredCurated = useMemo(
    () =>
      CURATED_COUNTRIES.flatMap(
        (curated) => filteredCountries.find((filtered) => filtered.code === curated.code) ?? [],
      ),
    [filteredCountries],
  )

  const filteredOthers = useMemo(() => {
    const curatedCodes = new Set(CURATED_COUNTRIES.map((c) => c.code))
    return filteredCountries.filter((country) => !curatedCodes.has(country.code))
  }, [filteredCountries])

  return (
    // Radix mirrors the value into a hidden native <select>. When the value changes before the
    // items have registered their <option>s, or has no option at all, that select reports ""
    // back through onValueChange. No item has an empty value, so "" never comes from the user.
    <Select
      value={selectedValue}
      onValueChange={(next) => {
        if (next) onChange(next)
      }}
      onOpenChange={(open) => {
        if (!open) setSearchQuery('')
      }}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger id={id} onBlur={onBlur} aria-label={name} className="w-full">
        <SelectValue placeholder={placeholder ?? m.selectACountry()} />
      </SelectTrigger>
      <SelectContent>
        <div className="sticky top-0 z-10 bg-popover p-1">
          <Input
            placeholder={m.searchCountries()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
            }}
          />
        </div>

        {showCuratedSection && filteredCurated.length > 0 && (
          <>
            <SelectGroup>
              <SelectLabel>{m.recommended()}</SelectLabel>
              {filteredCurated.map((country) => (
                <SelectItem key={country.code} value={country.name}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectGroup>
            {filteredOthers.length > 0 && <SelectSeparator />}
          </>
        )}

        {filteredOthers.length > 0 && (
          <SelectGroup>
            {showCuratedSection && <SelectLabel>{m.allCountries()}</SelectLabel>}
            {filteredOthers.map((country) => (
              <SelectItem key={country.code} value={country.name}>
                {country.label}
              </SelectItem>
            ))}
          </SelectGroup>
        )}

        {filteredCountries.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {m.noCountriesFound()}
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
